import React, { useEffect, useState } from "react";
import { acceptSecretSettings, refreshSecretCredentials } from "./credentials";
import "./secret-broker.css";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

export default function SecretBrokerCredentials({ onSaved }) {
  const [config, setConfig] = useState(null);
  const [forms, setForms] = useState({ dhan: { clientId: "", accessToken: "" }, upstox: { userId: "", accessToken: "" } });
  const [open, setOpen] = useState({ dhan: true, upstox: true });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const request = async (broker, body) => {
    const response = await fetch(`${API_BASE}/api/secret-broker/settings${broker ? `/${broker}` : ""}`, {
      method: broker ? "PUT" : "GET", cache: "no-store",
      headers: { Authorization: `Bearer ${localStorage.getItem("ct_login_token") || ""}`, "Content-Type": "application/json" },
      ...(broker ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok || !data.success) throw new Error(data.error || "Unable to save credentials");
    return data;
  };
  useEffect(() => {
    let cancelled = false;
    request().then(data => { if (!cancelled) { setConfig(data); setForms({ dhan: data.dhan, upstox: data.upstox }); } })
      .catch(failure => { if (!cancelled) setError(failure.message); });
    return () => { cancelled = true; };
  }, []);

  const save = async (broker, enabled) => {
    setBusy(true); setMessage(""); setError("");
    try {
      const data = await request(broker, { ...(enabled !== false ? forms[broker] : {}), ...(typeof enabled === "boolean" ? { enabled } : {}) });
      setConfig(data);
      if (enabled !== false) setForms(value => ({ ...value, [broker]: data[broker] }));
      acceptSecretSettings(data.settings);
      await refreshSecretCredentials();
      await onSaved?.();
      setMessage(`${broker === "dhan" ? "Dhan" : "Upstox"} credentials saved${typeof enabled === "boolean" ? enabled ? " · ON" : " · OFF" : ""}`);
    } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };
  return <div className="secret-broker-settings">
    {["dhan", "upstox"].map(broker => {
      const enabled = config?.activeBroker === broker;
      const name = broker === "dhan" ? "Dhan" : "Upstox";
      const id = broker === "dhan" ? "clientId" : "userId";
      return <section className="secret-broker-card" key={broker}>
        <div className="secret-broker-heading">
          <button type="button" className="secret-broker-title" onClick={() => setOpen(value => ({ ...value, [broker]: !value[broker] }))}>
            <strong>{broker === "dhan" ? "Secret Page Credentials · Dhan" : "Upstox Credentials"}</strong>
            <span>{open[broker] ? "Hide ▴" : "Show ▾"}</span>
          </button>
          <button type="button" role="switch" aria-label={`Use ${name} for Secret Page data`} aria-checked={enabled}
            disabled={busy || !config} className={`secret-broker-switch ${enabled ? "is-on" : ""}`}
            onClick={() => save(broker, !enabled)}><span />{enabled ? "ON" : "OFF"}</button>
        </div>
        {open[broker] && <>
          <div className="secret-broker-fields">
            <label>{name} {broker === "dhan" ? "Client ID" : "User ID (optional)"}
              <input value={forms[broker]?.[id] || ""} autoComplete="off"
                onChange={event => setForms(value => ({ ...value, [broker]: { ...value[broker], [id]: event.target.value } }))}
                placeholder={broker === "dhan" ? "Dhan Client ID" : "Upstox User ID / UCC"} />
            </label>
            <label>{name} Access Token
              <input type="password" value={forms[broker]?.accessToken || ""} autoComplete="new-password"
                onChange={event => setForms(value => ({ ...value, [broker]: { ...value[broker], accessToken: event.target.value } }))}
                placeholder={broker === "upstox" ? "Access Token or Analytics Token" : "Dhan Access Token"} />
            </label>
          </div>
          {broker === "upstox" && <p className="secret-broker-note">User ID means your Upstox account ID (UCC), not the API key. Market data requires the token; User ID is optional.</p>}
          <button className="secret-broker-save" type="button" disabled={busy || !config} onClick={() => save(broker)}>Save {name} Credentials</button>
        </>}
      </section>;
    })}
    <p className="secret-broker-note">Turning one provider ON turns the other OFF. OFF keeps its saved credentials and stops passing them to Secret Page data requests.</p>
    {error && <p className="secret-broker-error" role="alert">{error}</p>}
    {message && <p className="secret-broker-success" role="status">{message}</p>}
  </div>;
}
